import { METRICS_TO_COMPARATORS } from 'modules/campaigns/alerts/components/constants';
import { CampaignTypeFilter } from 'modules/domains/campaign';
import { AlertStatus, MetricName, ThresholdType } from '../../constants';
import { BulkUpdateOption } from '../../modals/UpdateAlertsModal/AlertBulkUpdateSettings';
import {
  AlertFormattedForForm,
  AlertFormattedForSave,
  AlertMetricsByMedium,
  AlertsFormState,
  AllMessageMediums,
  CampaignAlert,
  DirtyFields,
  GraphQLAlertMetric,
  ParsedKey,
  SavedAlertsByMedium,
  THours,
  ThresholdInputValue,
  TimeDuration,
} from '../../types';
import {
  getMetricData,
  getTypeKey,
  parseAlertKey,
  parseHourFrequencyToDuration,
} from '../../utils';

const DEFAULT_ALERT_CREATION_THRESHOLD = 0;
const DEFAULT_ALERT_CREATION_FREQUENCY = 'PT24H';

const parseStatusForUpdate = (status: boolean): AlertStatus =>
  status ? AlertStatus.Enabled : AlertStatus.Disabled;

const parseThresholdForUpdate = (threshold: ThresholdInputValue | undefined): number =>
  Number(threshold) || DEFAULT_ALERT_CREATION_THRESHOLD;

const parseFrequencyForUpdate = (frequency: THours): TimeDuration =>
  parseHourFrequencyToDuration(frequency) || DEFAULT_ALERT_CREATION_FREQUENCY;

/**
 * Should apply to any alert updates, including enabling/disabling or
 * changing alert's frequency or duration
 */
const formatAlertForUpdate = (
  currentValues: AlertFormattedForForm,
  existingAlert: CampaignAlert,
  typeKey: ThresholdType,
): AlertFormattedForSave => {
  const { status: s, frequency: f } = currentValues;
  const t = currentValues[typeKey];
  const { id, campaignType, messageMedium, metricName, comparisonOperator } = existingAlert;

  return {
    id,
    status: parseStatusForUpdate(s),
    campaignType,
    messageMedium,
    metricName,
    comparisonOperator,
    threshold: parseThresholdForUpdate(t),
    frequency: parseFrequencyForUpdate(f),
  };
};

const parseThresholdForCreation = (
  isDirty: boolean | undefined,
  currValue: ThresholdInputValue | undefined,
): number => (isDirty ? parseThresholdForUpdate(currValue) : DEFAULT_ALERT_CREATION_THRESHOLD);

const parseFrequencyForCreation = (
  isDirty: boolean | undefined,
  currValue: AlertFormattedForForm['frequency'],
): TimeDuration =>
  isDirty ? parseHourFrequencyToDuration(currValue) : DEFAULT_ALERT_CREATION_FREQUENCY;

/**
 * Should only apply to alerts being enabled for the first time
 */
const formatAlertForCreation = ({
  campaignType,
  messageMedium,
  metricName,
  dirtyFields,
  currentValues,
  typeKey,
}: {
  campaignType: CampaignTypeFilter;
  messageMedium: AllMessageMediums;
  metricName: MetricName;
  dirtyFields: DirtyFields;
  currentValues: AlertFormattedForForm;
  typeKey: ThresholdType;
}): AlertFormattedForSave => {
  return {
    status: AlertStatus.Enabled,
    campaignType,
    messageMedium,
    metricName,
    comparisonOperator: METRICS_TO_COMPARATORS[metricName],
    threshold: parseThresholdForCreation(dirtyFields?.[typeKey], currentValues[typeKey]),
    frequency: parseFrequencyForCreation(dirtyFields?.frequency, currentValues.frequency),
  };
};

/**
 * Check if alert exists in the set of saved alerts
 */
const getExistingAlert = ({
  campaignType,
  messageMedium,
  metricName,
  savedAlerts,
}: {
  campaignType: CampaignTypeFilter;
  messageMedium: AllMessageMediums;
  metricName: MetricName;
  savedAlerts: SavedAlertsByMedium;
}): CampaignAlert | undefined =>
  savedAlerts?.[messageMedium as AllMessageMediums].find(
    (alert) => alert.campaignType === campaignType && alert.metricName === metricName,
  );

type Props = {
  campaignType: CampaignTypeFilter;
  messageMedium: AllMessageMediums;
  metricName: MetricName;
  dirtyFields: DirtyFields;
  existingAlert: CampaignAlert | undefined;
  currentValues: AlertFormattedForForm;
  metricMetadata: GraphQLAlertMetric | undefined;
};

export const parseAlertForSaving = ({
  campaignType,
  messageMedium,
  metricName,
  dirtyFields,
  existingAlert,
  currentValues,
  metricMetadata,
}: Props): AlertFormattedForSave => {
  const typeKey = getTypeKey(metricMetadata?.valueType);

  return existingAlert
    ? formatAlertForUpdate(currentValues, existingAlert, typeKey)
    : formatAlertForCreation({
        campaignType,
        messageMedium,
        metricName,
        dirtyFields,
        currentValues,
        typeKey,
      });
};

type GetUpdateAlertsPayloadProps = {
  values: AlertsFormState;
  allAlertMetrics: AlertMetricsByMedium;
  savedAlerts: SavedAlertsByMedium;
  dirtyFields: Record<string, DirtyFields>;
  bulkUpdateSetting: BulkUpdateOption;
};

export const getUpdateAlertsPayload = ({
  values,
  allAlertMetrics,
  savedAlerts,
  dirtyFields,
  bulkUpdateSetting,
}: GetUpdateAlertsPayloadProps) => {
  const alerts = Object.entries(dirtyFields).reduce((acc, curr): AlertFormattedForSave[] => {
    const [key, value] = curr;

    const parsedKey = parseAlertKey(key) as ParsedKey;
    const [messageMedium, campaignType, metricName] = parsedKey;

    const metricMetadata = getMetricData({ allAlertMetrics, messageMedium, metricName });
    const existingAlert = getExistingAlert({
      campaignType,
      messageMedium,
      metricName,
      savedAlerts,
    });

    const alert = parseAlertForSaving({
      messageMedium,
      campaignType,
      metricName,
      dirtyFields: value,
      existingAlert,
      currentValues: values[key],
      metricMetadata,
    });

    return [...acc, alert];
  }, [] as AlertFormattedForSave[]);

  const applyBulkUpdates = bulkUpdateSetting === BulkUpdateOption.ActiveFutureCampaigns;

  return { alerts, applyBulkUpdates };
};
